import { useState } from 'react'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import LoginIcon from '@mui/icons-material/Login'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined'
import { Alert, Box, Button, Card, CardContent, Container, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/useAuth'

function getAuthErrorCode(error: unknown): string | null {
  if (typeof error !== 'object' || error === null) return null
  const maybeError = error as {
    response?: {
      status?: number
      data?: { error?: string }
    }
    message?: string
  }
  return maybeError.response?.data?.error || (maybeError.response?.status === 409 ? 'EMAIL_EXISTS' : null) || maybeError.message || null
}

const AUTH_MODE = import.meta.env.VITE_AUTH_MODE || 'local'
const IS_LOCAL_AUTH = AUTH_MODE === 'local'

function getAuthErrorMessage(error: unknown, options?: { isLocalAuth?: boolean }): string {
  const code = getAuthErrorCode(error)
  if (code === 'EMAIL_EXISTS') {
    return 'That email already has an account. Signing in instead may work if the password is correct.'
  }
  if (code === 'INVALID_CREDENTIALS') {
    if (options?.isLocalAuth) {
      return 'That email and password did not match. This dev environment stores accounts locally, so a redeploy can reset them. If you were using dev before the refresh, create the account again.'
    }
    return 'That email and password did not match. Check the password or create an account first.'
  }
  if (code === 'MISSING_FIELDS') {
    return 'Enter both an email and a password.'
  }
  if (code === 'LOGIN_FAILED' || code === 'SIGNIN_FAILED' || code === 'SIGNUP_FAILED') {
    return 'Authentication failed. Try again.'
  }
  return typeof code === 'string' && code ? code : 'Authentication failed.'
}

const headingFont = 'Lexend, Inter, sans-serif'
const bodyFont = 'Source Sans 3, Inter, sans-serif'
const EXISTING_ACCOUNT_MESSAGE = 'That email already has an account in this dev environment. Sign in with the existing password, or use a different email address to create a new account.'

const processSteps = [
  {
    title: 'Start with the screener questions',
    detail: 'Begin with a short set of plain-English questions so the app only opens the sections that apply to this client.',
  },
  {
    title: 'Add figures and source files',
    detail: 'Enter the key amounts, then attach the statements and notes that support each figure.',
  },
  {
    title: 'Check the handoff pack',
    detail: 'Review the trial IR3 and the Handoff tab so the accountant receives one clean, organised pack.',
  },
]

const supportAreas = [
  'PAYE, interest, dividends, donations, and student-loan support',
  'Working for Families, home-office notes, and questionnaire context',
  'Property-sale, RLWT, overseas-income, and crypto evidence',
  'See your Trial IR3 filing and handoff items before you send anything on',
]

export default function Home() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading, login, signup, getUserEmail } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  const [pending, setPending] = useState(false)
  const [showDemoDownloads, setShowDemoDownloads] = useState(false)

  const handleAuth = async () => {
    setPending(true)
    setError(null)
    const normalizedEmail = email.trim().toLowerCase()
    try {
      if (mode === 'signup') {
        try {
          await signup(normalizedEmail, password)
        } catch (err) {
          if (getAuthErrorCode(err) === 'EMAIL_EXISTS') {
            setMode('signin')
            setError(EXISTING_ACCOUNT_MESSAGE)
            return
          }
          throw err
        }
      }
      await login(normalizedEmail, password)
      navigate('/workspaces')
    } catch (err) {
      setError(getAuthErrorMessage(err, { isLocalAuth: IS_LOCAL_AUTH }))
      if (mode === 'signup' && getAuthErrorCode(err) === 'INVALID_CREDENTIALS') {
        setMode('signin')
      }
    } finally {
      setPending(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: [
          'radial-gradient(circle at 12% 14%, rgba(8,145,178,0.14), transparent 24%)',
          'radial-gradient(circle at 88% 18%, rgba(5,150,105,0.12), transparent 18%)',
          'linear-gradient(180deg, #f9fbfc 0%, #f3f7f8 38%, #eef2f1 100%)',
        ].join(','),
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={{ xs: 4, md: 7 }} sx={{ py: { xs: 3, sm: 4, md: 7 }, position: 'relative' }}>
          <Stack direction="row" justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  bgcolor: '#0891B2',
                  boxShadow: '0 0 0 6px rgba(8,145,178,0.12)',
                }}
              />
              <Typography sx={{ fontFamily: headingFont, fontWeight: 700, letterSpacing: '-0.02em', color: '#123244' }}>
                TaxHandoff NZ
              </Typography>
            </Stack>

            <Button variant="outlined" onClick={() => navigate('/workspaces')} disabled={!isAuthenticated} sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
              Open workspaces
            </Button>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.18fr) minmax(320px, 430px)' },
              gap: { xs: 3, md: 4 },
              alignItems: 'start',
            }}
          >
            <Stack spacing={{ xs: 3, md: 3.5 }}>
              <Stack spacing={{ xs: 2, md: 2.5 }} sx={{ maxWidth: 760 }}>
                <Typography
                  component="h1"
                  sx={{
                    fontFamily: headingFont,
                    fontSize: { xs: '2.3rem', sm: '2.9rem', md: '4.3rem' },
                    lineHeight: { xs: 1, md: 0.94 },
                    letterSpacing: '-0.05em',
                    color: '#123244',
                    maxWidth: 720,
                    textWrap: 'balance',
                  }}
                >
                  Prepare your tax pack for handoff to your accountant
                </Typography>

                <Typography
                  sx={{
                    fontFamily: bodyFont,
                    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.24rem' },
                    lineHeight: 1.5,
                    color: alpha('#123244', 0.82),
                    maxWidth: { xs: '100%', md: 660 },
                  }}
                >
                  Gather the right answers, figures, notes, and supporting records in one place so your accountant receives a
                  structured NZ tax pack instead of a scattered chain of messages and attachments.
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
                  gap: 1.5,
                  maxWidth: 920,
                }}
              >
                {[
                  { icon: <FactCheckOutlinedIcon fontSize="small" />, label: 'Answer key questions' },
                  { icon: <DescriptionOutlinedIcon fontSize="small" />, label: 'Upload required documents' },
                  { icon: <TaskAltOutlinedIcon fontSize="small" />, label: 'See your Trial IR3 filing' },
                ].map((item) => (
                  <Card
                    key={item.label}
                    variant="outlined"
                    sx={{
                      borderColor: alpha('#164e63', 0.1),
                      bgcolor: alpha('#ffffff', 0.72),
                      boxShadow: 'none',
                    }}
                  >
                    <CardContent sx={{ p: 2.25 }}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box sx={{ color: '#0891B2', display: 'flex' }}>{item.icon}</Box>
                        <Typography sx={{ fontFamily: headingFont, fontSize: { xs: '0.95rem', md: '0.98rem' }, color: '#123244' }}>
                          {item.label}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Box>

              <Alert
                severity="info"
                sx={{
                  maxWidth: 760,
                  border: `1px solid ${alpha('#0891B2', 0.16)}`,
                  bgcolor: alpha('#ffffff', 0.66),
                  '& .MuiAlert-message': { fontFamily: bodyFont, fontSize: { xs: '0.95rem', md: '1rem' }, lineHeight: 1.45 },
                }}
              >
                TaxHandoff NZ prepares an accountant handoff pack and a trial IR3 summary. It does not file with Inland Revenue on your behalf.
              </Alert>

              {isAuthenticated ? (
                <Alert
                  severity="success"
                  sx={{
                    maxWidth: 760,
                    bgcolor: alpha('#ffffff', 0.72),
                    '& .MuiAlert-message': { fontFamily: bodyFont, fontSize: { xs: '0.95rem', md: '1rem' } },
                  }}
                >
                  Signed in as {getUserEmail()}. Your tax records are ready to continue.
                </Alert>
              ) : null}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.25} sx={{ alignSelf: 'flex-start', opacity: 0.42 }}>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setShowDemoDownloads(true)}
                  sx={{ alignSelf: 'flex-start', color: alpha('#123244', 0.44), px: 0.25, py: 0, minWidth: 0, fontSize: '0.72rem', textTransform: 'none', lineHeight: 1.1 }}
                >
                  demo pack
                </Button>
                <Button
                  component="a"
                  href="/demo-downloads/DEMO_PRESENTER_SCRIPT.md"
                  download
                  variant="text"
                  size="small"
                  sx={{ alignSelf: 'flex-start', color: alpha('#123244', 0.44), px: 0.25, py: 0, minWidth: 0, fontSize: '0.72rem', textTransform: 'none', lineHeight: 1.1 }}
                >
                  presenter script
                </Button>
              </Stack>
            </Stack>

            <Card
              sx={{
                bgcolor: alpha('#fffdf8', 0.9),
                border: `1px solid ${alpha('#123244', 0.1)}`,
                boxShadow: '0 18px 50px rgba(18, 50, 68, 0.10)',
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 3.5 } }}>
                <Stack spacing={2.25}>
                  <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                    <Box>
                      <Typography sx={{ fontFamily: headingFont, fontSize: { xs: '1.35rem', md: '1.5rem' }, color: '#123244', mb: 0.5 }}>
                        {mode === 'signup' ? 'Create your account' : 'Sign in to continue'}
                      </Typography>
                      <Typography sx={{ fontFamily: bodyFont, color: alpha('#123244', 0.74), lineHeight: 1.45, fontSize: { xs: '0.98rem', md: '1rem' } }}>
                        Sign in to return to the tax information you are preparing for your accountant.
                      </Typography>
                    </Box>
                    <ShieldOutlinedIcon sx={{ color: '#059669' }} aria-hidden="true" />
                  </Stack>

                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    helperText="Use the email address attached to your saved workspaces."
                    fullWidth
                  />
                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    helperText="You will use this each time you come back to this tax workspace."
                    fullWidth
                  />

                  {error ? <Alert severity="error">{error}</Alert> : null}

                  <Stack spacing={1.25}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleAuth}
                      disabled={!email || !password || pending || isLoading}
                      fullWidth
                      startIcon={<LoginIcon />}
                      sx={{
                        bgcolor: '#059669',
                        boxShadow: '0 12px 24px rgba(5, 150, 105, 0.18)',
                        '&:hover': { bgcolor: '#047857' },
                      }}
                    >
                      {pending || isLoading ? 'Working…' : mode === 'signup' ? 'Create account and sign in' : 'Sign in'}
                    </Button>
                    <Button variant="outlined" onClick={() => setMode(mode === 'signup' ? 'signin' : 'signup')} disabled={pending || isLoading} fullWidth>
                      {mode === 'signup' ? 'Switch to sign in' : 'Create a new account'}
                    </Button>
                    <Button variant="text" onClick={() => navigate('/workspaces')} disabled={!isAuthenticated} fullWidth>
                      Open workspaces
                    </Button>
                  </Stack>

                  <Box
                    sx={{
                      mt: 0.5,
                      pt: 2,
                      borderTop: `1px solid ${alpha('#123244', 0.1)}`,
                    }}
                  >
                    <Typography sx={{ fontFamily: headingFont, fontSize: '0.95rem', color: '#123244', mb: 1.25 }}>
                      What to gather before sending to your accountant
                    </Typography>
                    <Stack spacing={1}>
                      {supportAreas.map((item) => (
                        <Stack key={item} direction="row" spacing={1} alignItems="flex-start">
                          <TaskAltOutlinedIcon sx={{ color: '#0891B2', fontSize: 19, mt: '2px' }} aria-hidden="true" />
                          <Typography sx={{ fontFamily: bodyFont, color: alpha('#123244', 0.78), lineHeight: 1.35, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {processSteps.map((step, index) => (
              <Card
                key={step.title}
                variant="outlined"
                sx={{
                  bgcolor: alpha('#ffffff', 0.76),
                  borderColor: alpha('#123244', 0.08),
                  boxShadow: 'none',
                }}
              >
                <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                  <Stack spacing={1.5}>
                    <Typography sx={{ fontFamily: headingFont, fontSize: '0.9rem', color: '#0891B2' }}>
                      0{index + 1}
                    </Typography>
                    <Typography sx={{ fontFamily: headingFont, fontSize: { xs: '1.2rem', md: '1.35rem' }, lineHeight: 1.08, color: '#123244' }}>
                      {step.title}
                    </Typography>
                    <Typography sx={{ fontFamily: bodyFont, fontSize: { xs: '0.98rem', md: '1.03rem' }, lineHeight: 1.48, color: alpha('#123244', 0.8) }}>
                      {step.detail}
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Box>

          <Card
            sx={{
              bgcolor: '#123244',
              color: 'white',
              overflow: 'hidden',
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 0.9fr) minmax(0, 1.1fr)' },
                  gap: 3,
                  alignItems: 'start',
                }}
              >
                <Stack spacing={2}>
                  <Typography sx={{ fontFamily: headingFont, fontSize: { xs: '1.8rem', sm: '2rem', md: '2.8rem' }, lineHeight: 0.98, letterSpacing: '-0.04em' }}>
                    Keep the accountant handoff clear.
                  </Typography>
                  <Typography sx={{ fontFamily: bodyFont, fontSize: { xs: '1rem', md: '1.08rem' }, lineHeight: 1.55, color: alpha('#ffffff', 0.82), maxWidth: 520 }}>
                    The process is simple: answer the right questions, attach the source records, and hand over one clear pack for accountant review.
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                    gap: 1.5,
                  }}
                >
                  {[
                    {
                      title: 'Plain-English wording',
                      detail: 'The page stays focused on the information your accountant needs, without product language or filler.',
                    },
                    {
                      title: 'Clear expectations',
                      detail: 'It explains that the client is preparing records for accountant review, not replacing advice or filing directly.',
                    },
                    {
                      title: 'Accountant-first handoff',
                      detail: 'Everything points toward giving your accountant a cleaner starting point for review.',
                    },
                    {
                      title: 'Readable on phone and PC',
                      detail: 'The handoff summary and next steps stay easy to read on mobile and desktop.',
                    },
                  ].map((item) => (
                    <Box
                      key={item.title}
                      sx={{
                        border: `1px solid ${alpha('#ffffff', 0.12)}`,
                        bgcolor: alpha('#ffffff', 0.06),
                        borderRadius: 2,
                        p: 2,
                      }}
                    >
                      <Typography sx={{ fontFamily: headingFont, mb: 0.75, fontSize: { xs: '1rem', md: '1.05rem' } }}>{item.title}</Typography>
                      <Typography sx={{ fontFamily: bodyFont, color: alpha('#ffffff', 0.76), lineHeight: 1.45, fontSize: { xs: '0.95rem', md: '1rem' } }}>
                        {item.detail}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>

        </Stack>
      </Container>

      <Dialog open={showDemoDownloads} onClose={() => setShowDemoDownloads(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Demo deck and sample files</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Use these downloads to rehearse the updated screener-first flow, show the front screen, and open sample records during the demo.
            </Typography>
            <Button component="a" href="/demo-downloads/nz-tax-demo-bundle.zip" download variant="contained" startIcon={<DownloadIcon />}>
              Download full demo bundle
            </Button>
            <Button component="a" href="/demo-downloads/sarah-carter-demo-kit.zip" download variant="outlined" startIcon={<DownloadIcon />}>
              Download salary-earner demo kit
            </Button>
            <Button component="a" href="/demo-downloads/james-walker-demo-kit.zip" download variant="outlined" startIcon={<DownloadIcon />}>
              Download property and crypto demo kit
            </Button>
            <Button component="a" href="/demo-downloads/DEMO_PRESENTER_SCRIPT.md" download variant="text">
              Download demo deck only
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDemoDownloads(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
